import {
  createContext,
  useReducer,
  useContext,
  useState,
  useEffect,
  useRef,
} from 'react';

import type { Dispatch, ReactNode } from 'react';
import type {
  IUnreadInAppMessageData,
  ISuggestedProductModal,
  BoostModalData,
  SellerAnalyticsModalDisplayType,
  ConsentOptions,
} from '../types';
import type { UserReducerAction } from '../contexts/reducers/userReducer';

import { userReducer } from '../contexts/reducers/userReducer';
import { useRouter } from 'next/router';
import type { NotificationFrequency } from '../components/SearchPage/hooks/useSavedSearch/useSavedSearch.typed';
import { signIn, signOut, useSession } from 'next-auth/react';
import { fireToast } from '../helpers/Toasts';
import { keycloakLogout } from '../utils/keycloakLogout';
import { UserProfile } from '../api/schemas/user-profile.schema';
import { userLogout } from '../helpers/Logout';
import { setupAuthInterceptor } from '../api/api-utils/commonApiUtils';
import { isProtectedRoute } from '../constants/protectedRoutes';
import { logger } from '@dist-marketplace-frontend/services';

interface UserProviderProps {
  children: ReactNode;
  initialUser: UserProfile | undefined;
  consentOptions: ConsentOptions;
}

interface IHistoryCheckModalData {
  title?: string;
}

interface UserContextI {
  userProfile: UserProfile | undefined;
  dispatchUserProfile: Dispatch<UserReducerAction>;
  isShareModalOpen: boolean;
  setIsShareModalOpen: (t: boolean) => void;
  consentOptions: ConsentOptions;
  isInAppMessageSimpleModalOpen: boolean;
  setIsInAppMessageSimpleModalOpen: (t: boolean) => void;
  inAppMessageData?: IUnreadInAppMessageData;
  setInAppMessageData: (t: IUnreadInAppMessageData | undefined) => void;
  isBoostModalOpen: boolean;
  setIsBoostModalOpen: (t: boolean) => void;
  boostModalData: BoostModalData | undefined;
  setBoostModalData: (t: BoostModalData | undefined) => void;
  isSuggestedProductModalOpen: boolean;
  setIsSuggestedProductModalOpen: (t: boolean) => void;
  suggestedProductModalData?: ISuggestedProductModal;
  setSuggestedProductModalData: (t: ISuggestedProductModal | undefined) => void;
  currency: 'EUR' | 'GBP' | undefined;
  setCurrency: (t: 'EUR' | 'GBP' | undefined) => void;
  selectedBundle?: number;
  setSelectedBundle: (bundleID: number | undefined) => void;
  isHistoryCheckModalOpen: boolean;
  setIsHistoryCheckModalOpen: (t: boolean) => void;
  historyCheckModalData: IHistoryCheckModalData | undefined;
  setHistoryCheckModalData: (t: IHistoryCheckModalData | undefined) => void;
  isSavedSearchModalOpen: boolean;
  setIsSavedSearchModalOpen: (t: boolean) => void;
  isInstantAlertAvailable: boolean;
  setIsInstantAlertAvailable: (t: boolean) => void;
  isSellerAnalyticsModalOpen: boolean;
  setIsSellerAnalyticsModalOpen: (t: boolean) => void;
  sellerAnalyticsModalDisplay?: SellerAnalyticsModalDisplayType;
  setSellerAnalyticsModalDisplay: (
    t: SellerAnalyticsModalDisplayType | undefined,
  ) => void;
  isSearchSaved: boolean;
  setIsSearchSaved: (saved: boolean) => void;
  selectedNotificationFrequency?: NotificationFrequency;
  setSelectedNotificationFrequency: (frequency: NotificationFrequency) => void;
  isTrustedDealerModalOpen: boolean;
  setIsTrustedDealerModalOpen: (t: boolean) => void;
  isBrazeInitialized: boolean;
  setIsBrazeInitialized: (t: boolean) => void;
  isUserVerified: boolean;
  isWriteOffModalOpen: boolean;
  setIsWriteOffModalOpen: (t: boolean) => void;
}

const UserContext = createContext<UserContextI>({
  userProfile: undefined,
  dispatchUserProfile: () => null,
  isShareModalOpen: false,
  setIsShareModalOpen: () => null,
  isInAppMessageSimpleModalOpen: false,
  setIsInAppMessageSimpleModalOpen: () => null,
  inAppMessageData: undefined,
  setInAppMessageData: () => null,
  isBoostModalOpen: false,
  setIsBoostModalOpen: () => null,
  boostModalData: undefined,
  setBoostModalData: () => null,
  isSuggestedProductModalOpen: false,
  setIsSuggestedProductModalOpen: () => null,
  suggestedProductModalData: undefined,
  setSuggestedProductModalData: () => null,
  currency: undefined,
  setCurrency: () => null,
  selectedBundle: undefined,
  setSelectedBundle: () => null,
  isHistoryCheckModalOpen: false,
  setIsHistoryCheckModalOpen: () => null,
  historyCheckModalData: undefined,
  setHistoryCheckModalData: () => null,
  isSavedSearchModalOpen: false,
  setIsSavedSearchModalOpen: () => null,
  isInstantAlertAvailable: false,
  setIsInstantAlertAvailable: () => null,
  isSellerAnalyticsModalOpen: false,
  setIsSellerAnalyticsModalOpen: () => null,
  sellerAnalyticsModalDisplay: undefined,
  setSellerAnalyticsModalDisplay: () => null,
  consentOptions: {
    functionalConsent: false,
    allowGoogle: false,
    allowWootric: false,
    allowBraze: false,
    allowConvert: false,
  },
  isSearchSaved: false,
  setIsSearchSaved: () => null,
  selectedNotificationFrequency: undefined,
  setSelectedNotificationFrequency: () => null,
  isTrustedDealerModalOpen: false,
  setIsTrustedDealerModalOpen: () => null,
  isBrazeInitialized: false,
  setIsBrazeInitialized: () => null,
  isUserVerified: false,
  isWriteOffModalOpen: false,
  setIsWriteOffModalOpen: () => null,
});

const useAuthOptions = () => {
  const { data: session, status } = useSession();
  const router = useRouter();
  const asPath = router.asPath;
  const isLoggedInKeycloakUser = !!session?.user;
  const shouldReturnAccessToken = !!session?.user && !session?.error;

  const { dispatchUserProfile } = useUserContext();

  const handleLogin = (url?: string) => {
    signIn('keycloak', {
      callbackUrl:
        typeof url === 'string'
          ? `/signin?callbackUrl=${url}`
          : `/signin?callbackUrl=${asPath}`,
    });
  };

  const handleLogout = async (callbackUrl?: string) => {
    const username = session?.user?.name ?? '';

    try {
      await keycloakLogout(callbackUrl);
    } catch (error) {
      fireToast({
        type: 'ERROR',
        text: 'Oops! Something went wrong, please try again later',
      });
      return;
    }

    await userLogout(dispatchUserProfile, username);
  };

  return {
    handleLogin,
    handleLogout,
    isAuthenticated: isLoggedInKeycloakUser,
    accessToken: shouldReturnAccessToken ? session?.accessToken : undefined,
    status,
    user: session?.user,
  };
};

function UserProvider({
  children,
  initialUser,
  consentOptions,
}: UserProviderProps) {
  const [userProfile, dispatchUserProfile] = useReducer(
    userReducer,
    initialUser,
  );

  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isInAppMessageSimpleModalOpen, setIsInAppMessageSimpleModalOpen] =
    useState(false);
  const [inAppMessageData, setInAppMessageData] =
    useState<IUnreadInAppMessageData>();
  const [isBoostModalOpen, setIsBoostModalOpen] = useState<boolean>(false);
  const [boostModalData, setBoostModalData] = useState<
    BoostModalData | undefined
  >(undefined);
  const [isSuggestedProductModalOpen, setIsSuggestedProductModalOpen] =
    useState<boolean>(false);
  const [suggestedProductModalData, setSuggestedProductModalData] = useState<
    ISuggestedProductModal | undefined
  >(undefined);
  const [currency, setCurrency] = useState<'EUR' | 'GBP' | undefined>(
    undefined,
  );
  const [selectedBundle, setSelectedBundle] = useState<number | undefined>(
    undefined,
  );
  const [isHistoryCheckModalOpen, setIsHistoryCheckModalOpen] =
    useState<boolean>(false);
  const [historyCheckModalData, setHistoryCheckModalData] = useState<
    IHistoryCheckModalData | undefined
  >(undefined);
  const [isSavedSearchModalOpen, setIsSavedSearchModalOpen] =
    useState<boolean>(false);
  const [isInstantAlertAvailable, setIsInstantAlertAvailable] =
    useState<boolean>(false);
  const [isSellerAnalyticsModalOpen, setIsSellerAnalyticsModalOpen] =
    useState<boolean>(false);
  const [sellerAnalyticsModalDisplay, setSellerAnalyticsModalDisplay] =
    useState<SellerAnalyticsModalDisplayType | undefined>(undefined);
  const [isSearchSaved, setIsSearchSaved] = useState<boolean>(false);
  const [selectedNotificationFrequency, setSelectedNotificationFrequency] =
    useState<NotificationFrequency | undefined>();
  const [isTrustedDealerModalOpen, setIsTrustedDealerModalOpen] =
    useState<boolean>(false);
  const [isBrazeInitialized, setIsBrazeInitialized] = useState<boolean>(false);
  const [isWriteOffModalOpen, setIsWriteOffModalOpen] =
    useState<boolean>(false);

  const isUserVerified = Boolean(
    userProfile?.verification.email || userProfile?.verification.phone,
  );

  const { update: updateSession } = useSession();

  // Pin the unstable values behind refs so the interceptor setup effect runs
  // once — otherwise every session refetch would tear down and recreate the
  // interceptor, resetting its concurrent-refresh lock mid-flight.
  // Replace with useEffectEvent once we're on React 19.
  const updateSessionRef = useRef(updateSession);
  updateSessionRef.current = updateSession;

  const userProfileNameRef = useRef(userProfile?.name || '');
  userProfileNameRef.current = userProfile?.name || '';

  useEffect(() => {
    setupAuthInterceptor({
      onSessionRefreshSuccess: async () => {
        // At this point, the next-auth cookie is already refreshed by the interceptor,
        // This connects the interceptor's behavior back to next-auth in the client here.
        await updateSessionRef.current();
      },
      onSessionRefreshError: async () => {
        if (isProtectedRoute(location.pathname)) {
          signIn('keycloak');
          return;
        }

        await runOnceAcrossTabs({
          lockName: 'dd-logout',
          callback: async () => {
            try {
              await Promise.all([
                userLogout(dispatchUserProfile, userProfileNameRef.current),
                signOut({ redirect: false }),
              ]);
            } catch (error) {
              logger.error({
                message:
                  'An error occurred while trying to force the user to log out',
                error,
              });
            }
          },
        });
      },
    });
  }, [dispatchUserProfile]);

  return (
    <UserContext.Provider
      value={{
        userProfile,
        dispatchUserProfile,
        isShareModalOpen,
        setIsShareModalOpen,
        isInAppMessageSimpleModalOpen,
        setIsInAppMessageSimpleModalOpen,
        inAppMessageData,
        setInAppMessageData,
        isBoostModalOpen,
        setIsBoostModalOpen,
        boostModalData,
        setBoostModalData,
        isSuggestedProductModalOpen,
        setIsSuggestedProductModalOpen,
        suggestedProductModalData,
        setSuggestedProductModalData,
        currency,
        setCurrency,
        selectedBundle,
        setSelectedBundle,
        isHistoryCheckModalOpen,
        setIsHistoryCheckModalOpen,
        historyCheckModalData,
        setHistoryCheckModalData,
        isSavedSearchModalOpen,
        setIsSavedSearchModalOpen,
        isInstantAlertAvailable,
        setIsInstantAlertAvailable,
        isSellerAnalyticsModalOpen,
        setIsSellerAnalyticsModalOpen,
        sellerAnalyticsModalDisplay,
        setSellerAnalyticsModalDisplay,
        consentOptions,
        isSearchSaved,
        setIsSearchSaved,
        selectedNotificationFrequency,
        setSelectedNotificationFrequency,
        isTrustedDealerModalOpen,
        setIsTrustedDealerModalOpen,
        isBrazeInitialized,
        setIsBrazeInitialized,
        isUserVerified,
        isWriteOffModalOpen,
        setIsWriteOffModalOpen,
      }}
    >
      {children}
    </UserContext.Provider>
  );
}

const useUserContext = () => useContext(UserContext);

// NB: This won't work in Safari <= 15.4, it will fallback to running the callback on every tab
function runOnceAcrossTabs({
  lockName,
  callback,
}: {
  lockName: string;
  callback: () => void | Promise<void>;
}) {
  if (!navigator.locks) {
    return callback();
  }

  // ifAvailable: true means that we get lock === null if the lock is already taken by another tab
  // the default behaviour being to wait for the lock to be available
  return navigator.locks.request(
    lockName,
    { ifAvailable: true },
    async (lock) => {
      if (lock === null) {
        return undefined;
      }

      return callback();
    },
  );
}

export { UserProvider, useUserContext, useAuthOptions };
